import { Box, Checkbox, FormControlLabel, FormGroup, Stack, Typography } from '@mui/material';
import { ThemeProvider, useTheme } from '@mui/material/styles';
import { Controller, useFormContext } from 'react-hook-form';

import { AdFieldMultilistingEnum, CurrencyCommissionEnum } from '../../types/enums';
import type { PropertyAdEditorFormValues } from '../../types/propertyAdEditor';
import { customTheme } from '../../utils/customTheme';

import checkboxStyles from '../uiComponents/FormCheckbox/FormCheckbox.module.scss';
import { FormInput } from '../uiComponents/FormInput';
import { AiReviewFieldNotice, useAiListingReview } from './AiListingReview';
import { EditorSection } from './EditorSection';
import { EditorNumericField, EditorSelectField } from './fields';
import styles from './PropertyAdEditor.module.scss';

const multilistingOptions = [
  { label: 'Covered', value: AdFieldMultilistingEnum.Covered },
  { label: 'Not covered', value: AdFieldMultilistingEnum.NotCovered },
] as const;

const currencyCommissionOptions = [
  { label: 'AED', value: CurrencyCommissionEnum.AED },
  { label: 'USD', value: CurrencyCommissionEnum.USD },
  { label: '%', value: CurrencyCommissionEnum.Percent },
] as const;

export const CommissionConditionsSection = () => {
  const { control } = useFormContext<PropertyAdEditorFormValues>();
  const aiReview = useAiListingReview();
  const outerTheme = useTheme();

  return (
    <EditorSection
      cardTestId='commission-conditions-card'
      description='Set commission and additional listing conditions'
      title='Commission & Conditions'
      titleId='commission-conditions-title'
    >
      <AiReviewFieldNotice target='section:commission' />
      <Stack minWidth={0} spacing={3}>
        <Box maxWidth={440} minWidth={0} width='100%'>
          <EditorSelectField
            label='Do you receive a fee from your client?'
            name='multilisting'
            options={multilistingOptions}
            placeholder='Select value'
            required
            rules={{ required: 'Multilisting is a required field' }}
          />
        </Box>

        <Box
          sx={{
            display: 'grid',
            gap: 2.5,
            gridTemplateColumns: {
              xs: 'minmax(0, 1fr)',
              sm: 'minmax(0, 1fr) minmax(140px, 160px)',
            },
            minWidth: 0,
            width: '100%',
          }}
        >
          <Box className={styles.commissionInput} minWidth={0}>
            <EditorNumericField
              label='Additional Commission'
              name='addCommission'
              placeholder='Enter commission value'
            />
          </Box>

          <Box minWidth={0}>
            <EditorSelectField
              ariaLabel='Commission currency'
              label='Currency'
              name='currencyCommission'
              options={currencyCommissionOptions}
            />
          </Box>
        </Box>

        <Controller
          control={control}
          name='topUp'
          render={({ field }) => (
            <ThemeProvider theme={customTheme(outerTheme)}>
              <Stack alignItems='center' color='#2a3842' direction='row' minWidth={0}>
                <FormGroup>
                  <FormControlLabel
                    className={checkboxStyles.label}
                    control={
                      <Checkbox
                        checked={field.value}
                        checkedIcon={<span className={checkboxStyles.checked} />}
                        icon={<span />}
                        inputProps={{ 'aria-label': 'Top up (Price boost opportunity)' }}
                        inputRef={field.ref}
                        name={field.name}
                        onBlur={field.onBlur}
                        onChange={(_event, checked) => {
                          field.onChange(checked);
                          aiReview?.resolveField('topUp');
                        }}
                      />
                    }
                    label=''
                    sx={{ marginRight: 0 }}
                  />
                </FormGroup>
                <Typography color='#1650ff' component='span' variant='inherit'>
                  Top up
                </Typography>
                <Typography component='span' variant='inherit'>
                  &nbsp;(Price boost opportunity)
                </Typography>
              </Stack>
            </ThemeProvider>
          )}
        />
        <AiReviewFieldNotice target='topUp' />

        <Box className={styles.topUpConditionsField} minWidth={0}>
          <FormInput
            fullWidth
            inputProps={{ 'aria-label': 'Top up conditions' }}
            label='Top up conditions'
            minRows={3}
            multiline
            name='topUpConditions'
            onChange={() => aiReview?.resolveField('topUpConditions')}
            placeholder='Enter additional conditions'
            uiVariant='blank'
          />
          <AiReviewFieldNotice target='topUpConditions' />
        </Box>
      </Stack>
    </EditorSection>
  );
};
